import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface Category {
  id: string;
  name: string;
  nameEn: string | null;
  sortOrder: number;
  isVisible: boolean;
}

const INPUT =
  'h-10 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white';

export default function AdminCategories() {
  const [cats, setCats] = useState<Category[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [form, setForm] = useState({ id: '', name: '', sortOrder: 999 });
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    const [c, p] = await Promise.all([
      api<{ items: Category[] }>('/admin/categories'),
      api<{ items: { categoryId: string }[] }>('/admin/products'),
    ]);
    if (c.ok && c.data) {
      setCats(c.data.items);
      const cnt: Record<string, number> = {};
      if (p.ok && p.data) {
        for (const it of p.data.items) cnt[it.categoryId] = (cnt[it.categoryId] ?? 0) + 1;
      }
      setCounts(cnt);
    } else if (c.error) {
      setError(errMsg(c, '加载失败'));
    }
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  function openNew() {
    setError('');
    setNotice('');
    setForm({ id: '', name: '', sortOrder: 999 });
    setEditing('new');
  }

  function openEdit(c: Category) {
    setError('');
    setNotice('');
    setForm({ id: c.id, name: c.name, sortOrder: c.sortOrder });
    setEditing(c);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    const isNew = editing === 'new';
    const body = isNew
      ? { id: form.id.trim(), name: form.name.trim(), sortOrder: Number(form.sortOrder) }
      : { name: form.name.trim(), sortOrder: Number(form.sortOrder) };
    const res = isNew
      ? await api('/admin/categories', { method: 'POST', body: JSON.stringify(body) })
      : await api(`/admin/categories/${form.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
    setSaving(false);
    if (res.ok) {
      setEditing(null);
      setNotice(isNew ? '分类已新增' : '分类已更新');
      load();
    } else {
      setError(errMsg(res, '保存失败'));
    }
  }

  async function remove(c: Category) {
    if (!confirm(`确定删除分类「${c.name}」？`)) return;
    setError('');
    const res = await api(`/admin/categories/${c.id}`, { method: 'DELETE' });
    if (res.ok) {
      setNotice('已删除');
      load();
    } else {
      setError(errMsg(res, '删除失败'));
    }
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-500">
          管理道具分类：新增 / 编辑 / 删除。删除前请先将该分类下的道具转移或归档。
        </p>
        <button
          onClick={openNew}
          className="rounded-full bg-ink-900 px-5 py-2 text-xs text-ink-50"
        >
          + 新增分类
        </button>
      </div>

      {notice && (
        <p className="mb-4 rounded-lg bg-candle-300/20 px-4 py-2.5 text-sm text-ink-700">
          {notice}
        </p>
      )}
      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
      )}

      {loading ? (
        <p className="text-sm text-ink-500">加载中…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
              <tr>
                <th className="px-4 py-3">分类 ID</th>
                <th className="px-4 py-3">名称</th>
                <th className="px-4 py-3">排序</th>
                <th className="px-4 py-3">道具数</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-3 font-mono text-[11px] text-ink-400">{c.id}</td>
                  <td className="px-4 py-3 text-ink-900">{c.name}</td>
                  <td className="px-4 py-3 text-ink-500">{c.sortOrder}</td>
                  <td className="px-4 py-3 text-ink-500">{counts[c.id] ?? 0}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openEdit(c)}
                      className="mr-3 text-xs text-ink-700 underline"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => remove(c)}
                      className="text-xs text-red-600 underline"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6"
          onClick={() => setEditing(null)}
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl bg-white p-6"
          >
            <h3 className="mb-5 font-serifcn text-lg text-ink-900">
              {editing === 'new' ? '新增分类' : `编辑：${editing.name}`}
            </h3>

            <div className="space-y-4">
              {editing === 'new' && (
                <div>
                  <label className="mb-1.5 block text-xs text-ink-600">分类 ID</label>
                  <input
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    placeholder="cat_xxx"
                    className={INPUT}
                    required
                  />
                  <p className="mt-1 text-[11px] text-ink-400">
                    字母/数字/下划线，例如 cat_food，创建后不可修改
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-xs text-ink-600">名称</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="供品"
                  className={INPUT}
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block text-xs text-ink-600">
                  排序权重（越小越靠前）
                </label>
                <input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                  className={INPUT}
                />
              </div>
            </div>

            {error && (
              <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="h-11 flex-1 rounded-xl bg-ink-900 text-sm text-ink-50 disabled:opacity-50"
              >
                {saving ? '保存中…' : '保存'}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="h-11 rounded-xl border border-ink-200 px-6 text-sm text-ink-600"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
